import { Accordion, Button, Drawer, TextOrHTML } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames";
import { useAtom, useSetAtom } from "jotai";
import { useRef, useState } from "react";
import type { QuoteActionsProps } from ".";
import { paymentToggleAtom, sliderStateAtom } from "../../../../store";
import { Coverage } from "../Coverage";
import { transformExtraCoverage } from "../Coverage/transformCoverage";
import { Logo } from "../Logo";
import { Ratings } from "../Ratings";
import { AdditionalCoverageInformation } from "./QuoteDetails/AdditionalCoverageInformation/AdditionalCoverageInformation";
import { Footer } from "./QuoteDetails/Footer";
import { PaymentInformation } from "./QuoteDetails/PaymentInformation";
import { ReferredQuotesCallDisplay } from "./QuoteDetails/ReferredQuotesCallDisplay";

export const QuoteActions = ({ quote }: QuoteActionsProps) => {
  const { snowplow } = useSnowplowContext();
  const [paymentPeriod] = useAtom(paymentToggleAtom);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const setSliderIsOpen = useSetAtom(sliderStateAtom);
  const {
    installmentsAvailable,
    installmentsAmount,
    covers,
    vertical,
    additionalDisclosures,
    insurerLogo,
    ratings,
    insurerDetails,
    upsells,
    helpText,
    offlinePurchaseOnly,
  } = quote;
  const extraCovers = transformExtraCoverage(covers);
  const hasInstallments = Boolean(installmentsAvailable && installmentsAmount);
  const detailsActionClasses = classNames(
    "mobius-quote-actions__button",
    "mobius-quote-actions__button--is-details",
    "qa-view-details-option",
  );
  const selectActionClasses = classNames(
    "mobius-quote-actions__button",
    "mobius-quote-actions__button--is-select",
    "qa-buy-quote-option",
  );
  const [detailsLabel, selectLabel] = ["Details", "Select"];

  const openDrawer = (label: string) => {
    setDrawerOpen(true);
    setSliderIsOpen(true);
    snowplow?.trigger("sliderOpened", { label });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSliderIsOpen(false);
  };

  const showPaymentInformation = !offlinePurchaseOnly;

  const paymentInformationRef = useRef<HTMLDivElement>(null);

  const handleInsurerDetailsOpen = () => {
    snowplow?.trigger("insurerDetailsAccordionClicked");
  };

  return (
    <>
      <div className="mobius-quote-actions">
        <Button
          variant="secondary"
          className={detailsActionClasses}
          size="sm"
          onClick={() => openDrawer(detailsLabel)}
        >
          {detailsLabel}
        </Button>

        {quote.offlinePurchaseOnly ? (
          <ReferredQuotesCallDisplay quote={quote} displayVariant="qcp" />
        ) : (
          <Button
            className={selectActionClasses}
            size="sm"
            onClick={() => openDrawer(selectLabel)}
          >
            {selectLabel}
          </Button>
        )}
      </div>
      <Drawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        className="mobius-quote-details__drawer"
        closeLabel="Close"
        direction="right"
      >
        <Drawer.Header>
          <Logo logoPath={insurerLogo} />
        </Drawer.Header>
        <Drawer.Content>
          {ratings && <Ratings data={ratings} />}
          {insurerDetails && (
            <Accordion
              className="mobius-quote-details__insurer-container"
              showText="See more"
              hideText="See less"
              onOpen={handleInsurerDetailsOpen}
            >
              <TextOrHTML
                htmlClassName="mobius-quote-details__insurer"
                htmlElementType="div"
                text={insurerDetails}
              />
            </Accordion>
          )}
          {covers && (
            <Coverage
              helpText={helpText}
              covers={covers}
              extraCovers={extraCovers}
              upsells={upsells}
              displayVariant="qds"
              className="--has-divider"
              withModal
            />
          )}

          {additionalDisclosures &&
            Object.keys(additionalDisclosures).length > 0 && (
              <AdditionalCoverageInformation
                vertical={vertical}
                additionalDisclosures={additionalDisclosures}
              />
            )}

          {showPaymentInformation && (
            <PaymentInformation
              quote={quote}
              paymentPeriod={paymentPeriod}
              ref={paymentInformationRef}
            />
          )}
        </Drawer.Content>

        <Footer
          quote={quote}
          paymentInformationRef={paymentInformationRef}
          hasInstallments={hasInstallments}
        />
      </Drawer>
    </>
  );
};
